import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const ScopeContext = createContext(null);
const STORAGE_KEY = "fiper.scope";

export function ScopeProvider({ children }) {
  const { user } = useAuth();
  const [scope, setScopeState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "personal";
    } catch {
      return "personal";
    }
  });
  const [couple, setCouple] = useState(null);
  const [loadingCouple, setLoadingCouple] = useState(false);

  const setScope = useCallback((next) => {
    setScopeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_) { /* ignore */ }
  }, []);

  const loadCouple = useCallback(async () => {
    if (!user || user === false) {
      setCouple(null);
      return;
    }
    setLoadingCouple(true);
    try {
      const { data } = await api.get("/couple/state");
      setCouple(data);
    } catch (_) {
      setCouple(null);
    } finally {
      setLoadingCouple(false);
    }
  }, [user]);

  useEffect(() => {
    loadCouple();
  }, [loadCouple]);

  // If user loses household, force back to personal scope
  useEffect(() => {
    if (couple && !couple.household && scope === "shared") {
      setScope("personal");
    }
  }, [couple, scope, setScope]);

  const value = useMemo(() => ({
    scope, setScope, couple, loadingCouple, reloadCouple: loadCouple,
    hasHousehold: !!couple?.household,
    partner: couple?.partner || null,
  }), [scope, setScope, couple, loadingCouple, loadCouple]);

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useScope() {
  const ctx = useContext(ScopeContext);
  if (!ctx) throw new Error("useScope must be used within ScopeProvider");
  return ctx;
}
